<template>
  <div class="retailer-page-container">
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner">
        <font-awesome-icon :icon="['fas', 'sync']" class="fa-spin" />
        <span>Loading data...</span>
      </div>
    </div>

    <div v-else>
      <ConfirmationDialogBox
        :title="'Remove NMI from monitoring? This action cannot be undone.'"
        :isVisible="isDialogVisible"
        @confirmed="handleConfirmed"
        @cancelled="handleCancelled"
      />

      <ModalBox
        :visible="showModal"
        :infoTitle="infoTitle"
        :infoMessage="infoMessage"
        @close="showModal = false"
      />

      <!-- Header Card -->
      <div class="plant-info-card">
        <div class="plant-info-header header-with-actions">
          <div>
            <div class="plant-info-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 3v18h18"/>
                <path d="M7 15l3-3 3 2 4-5"/>
              </svg>
              <h4>NMI Comparison Dashboard</h4>
            </div>
            <p class="page-subtitle">
              Compare NMIs across Renox plant data, LocalVolts retailer feed, Amber AI pool members, and the Renox AI pool.
            </p>
          </div>
          <div class="refresh-container">
            <span v-if="lastRefresh" class="last-updated">Last updated: {{ formatDate(lastRefresh) }}</span>
            <button @click="refreshData" class="custom-button refresh-button">
              <font-awesome-icon :icon="['fas', 'sync']" :class="{ 'fa-spin': isLoading }" />
              Refresh Data
            </button>
          </div>
        </div>
      </div>

      <!-- Global NMI Lookup -->
      <div class="plant-info-card">
        <div class="plant-info-header">
          <div class="plant-info-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <h4>Global NMI Lookup</h4>
          </div>
        </div>
        <p class="page-subtitle">
          Enter an NMI to see which data sources contain it.
        </p>
        <div class="lookup-search-row">
          <input
            type="text"
            v-model="globalNmiSearch"
            placeholder="Search by NMI..."
            class="search-input lookup-input"
          />
          <button
            v-if="globalNmiSearch"
            class="custom-button clear-button"
            @click="clearNmiLookup"
          >
            Clear
          </button>
        </div>

        <div v-if="!effectiveNmiLookupQuery" class="lookup-hint">
          Start typing an NMI to see results.
        </div>
        <div v-else class="lookup-results">
          <p class="lookup-query-label">Results for <strong>{{ effectiveNmiLookupQuery }}</strong></p>
          <div class="lookup-results-list">
            <div
              v-for="row in nmiLookupRows"
              :key="row.key"
              class="lookup-result-line"
              :class="{ present: row.present, absent: !row.present }"
            >
              <span class="lookup-line-label">{{ row.label }}</span>
              <span class="lookup-line-status">
                <font-awesome-icon
                  v-if="row.present"
                  :icon="['fas', 'circle-check']"
                  class="icon-present"
                />
                <font-awesome-icon
                  v-else
                  :icon="['fas', 'link-slash']"
                  class="icon-absent"
                />
                {{ row.present ? 'Found' : 'Not found' }}
              </span>
              <span v-if="row.detail" class="lookup-line-detail">{{ row.detail }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats Card -->
      <div class="plant-info-card">
        <div class="plant-info-header">
          <div class="plant-info-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="20" x2="18" y2="10"/>
              <line x1="12" y1="20" x2="12" y2="4"/>
              <line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
            <h4>Summary</h4>
          </div>
        </div>
        <div class="kpi-grid">
          <button
            v-for="card in summaryCards"
            :key="card.tabName"
            type="button"
            class="custom-kpi-card"
            :class="[card.cardClass, { 'kpi-card-active': activeTab === card.tabName }]"
            @click="goToTab(card.tabName)"
          >
            <div class="kpi-icon">
              <font-awesome-icon :icon="card.icon" />
            </div>
            <div class="kpi-content">
              <div class="kpi-value">{{ card.value }}</div>
              <div class="kpi-label">{{ card.label }}</div>
            </div>
          </button>
        </div>
      </div>

      <!-- Tabs -->
      <div ref="tabsContainer" class="date-range-tabs-container">
        <div class="tabs-header">
          <button
            v-for="tab in tabs"
            :key="tab.name"
            :class="{ active: activeTab === tab.name }"
            @click="activeTab = tab.name"
            class="tab-button"
          >
            {{ tab.label }}
          </button>
        </div>
        <div class="tab-content-panel">
          <p class="tab-description">{{ activeTabDescription }}</p>

          <!-- Matching Tab -->
          <div v-if="activeTab === 'matchingTab'" class="tab-table-section">
            <div class="table-header">
              <h5>Matching NMIs</h5>
              <div class="search-container">
                <input type="text" v-model="searchMatchingNMI" placeholder="Filter NMIs..." class="search-input" />
              </div>
            </div>
            <div class="table-container">
              <table class="full-width-table">
                <thead>
                  <tr>
                    <th @click="sortTable('matching', 'nmi')">NMI <font-awesome-icon :icon="getSortIcon('nmi', 'matching')" /></th>
                    <th @click="sortTable('matching', 'plant_id')">Renox Plant ID <font-awesome-icon :icon="getSortIcon('plant_id', 'matching')" /></th>
                    <th @click="sortTable('matching', 'plant_name')">Plant Name <font-awesome-icon :icon="getSortIcon('plant_name', 'matching')" /></th>
                    <th>AI Pool Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in filteredMatchingNMIs" :key="index">
                    <td>{{ item.nmi }}</td>
                    <td>{{ item.plant_id }}</td>
                    <td>{{ item.plant_name }}</td>
                    <td>
                      <span v-if="isInPool(item.plant_id)" class="status-indicator">
                        <span class="status-circle status-active"></span>
                        In Pool
                      </span>
                      <span v-else class="status-indicator">
                        <span class="status-circle status-inactive"></span>
                        Not in Pool
                      </span>
                    </td>
                    <td class="actions-cell">
                      <button
                        type="button"
                        class="action-link-button"
                        @click.stop="openRenoxPowerPortal(item)"
                      >
                        Open in Renox Power portal
                      </button>
                      <button
                        v-if="isInPool(item.plant_id)"
                        type="button"
                        class="action-link-button action-link-button-ai"
                        @click.stop="openRenoxAiPortal(item)"
                      >
                        Open in Renox AI portal
                      </button>
                    </td>
                  </tr>
                  <tr v-if="filteredMatchingNMIs.length === 0">
                    <td colspan="5" class="no-data">No matching NMIs found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Non-Matching LocalVolts Tab -->
          <div v-if="activeTab === 'nonMatchingLocalVoltsTab'" class="tab-table-section">
            <div class="table-header">
              <h5>All Non-Matching LocalVolts NMIs</h5>
              <div class="search-container">
                <input type="text" v-model="searchNonMatchingLocalVoltsNMI" placeholder="Filter NMIs..." class="search-input" />
              </div>
            </div>
            <div class="table-container">
              <table class="full-width-table">
                <thead>
                  <tr>
                    <th @click="sortTable('nonMatching', 'nmi')">NMI <font-awesome-icon :icon="getSortIcon('nmi', 'nonMatching')" /></th>
                    <th @click="sortTable('nonMatching', 'source')">Source <font-awesome-icon :icon="getSortIcon('source', 'nonMatching')" /></th>
                    <th @click="sortTable('nonMatching', 'missing_in')">Missing In <font-awesome-icon :icon="getSortIcon('missing_in', 'nonMatching')" /></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in filteredNonMatchingLocalVoltsNMIs" :key="index">
                    <td>{{ item.nmi }}</td>
                    <td>{{ item.source }}</td>
                    <td>{{ item.missing_in }}</td>
                  </tr>
                  <tr v-if="filteredNonMatchingLocalVoltsNMIs.length === 0">
                    <td colspan="3" class="no-data">No non-matching LocalVolts NMIs found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- All LocalVolts NMIs Tab -->
          <div v-if="activeTab === 'localVoltsAllTab'" class="tab-table-section">
            <div class="table-header">
              <h5>All LocalVolts NMIs</h5>
              <div class="search-container">
                <input type="text" v-model="searchAllLocalVoltsNMI" placeholder="Filter NMIs..." class="search-input" />
              </div>
            </div>
            <div class="table-container">
              <table class="full-width-table">
                <thead>
                  <tr>
                    <th @click="sortTable('localVoltsNMIs', 'nmi')">NMI <font-awesome-icon :icon="getSortIcon('nmi', 'localVoltsNMIs')" /></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in filteredAllLocalVoltsNMIs" :key="index">
                    <td>{{ item.nmi }}</td>
                  </tr>
                  <tr v-if="filteredAllLocalVoltsNMIs.length === 0">
                    <td class="no-data">No NMIs found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- All Renox NMIs Tab -->
          <div v-if="activeTab === 'plantAllTab'" class="tab-table-section">
            <div class="table-header">
              <h5>All Renox NMIs</h5>
              <div class="search-container">
                <input type="text" v-model="searchAllPlantNMI" placeholder="Filter NMIs..." class="search-input" />
              </div>
            </div>
            <div class="table-container">
              <table class="full-width-table">
                <thead>
                  <tr>
                    <th @click="sortTable('plantNMIs', 'nmi')">NMI <font-awesome-icon :icon="getSortIcon('nmi', 'plantNMIs')" /></th>
                    <th @click="sortTable('plantNMIs', 'plant_id')">Renox Plant ID <font-awesome-icon :icon="getSortIcon('plant_id', 'plantNMIs')" /></th>
                    <th @click="sortTable('plantNMIs', 'plant_name')">Plant Name <font-awesome-icon :icon="getSortIcon('plant_name', 'plantNMIs')" /></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in filteredAllPlantNMIs" :key="index">
                    <td>{{ item.nmi }}</td>
                    <td>{{ item.plant_id }}</td>
                    <td>{{ item.plant_name }}</td>
                  </tr>
                  <tr v-if="filteredAllPlantNMIs.length === 0">
                    <td colspan="3" class="no-data">No NMIs found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Amber NMIs Tab -->
          <div v-if="activeTab === 'amberTab'" class="tab-table-section">
            <div class="table-header">
              <h5>All Amber NMIs</h5>
              <div class="search-container">
                <input type="text" v-model="searchAmberNMI" placeholder="Filter NMIs..." class="search-input" />
              </div>
            </div>
            <div class="table-container">
              <table class="full-width-table">
                <thead>
                  <tr>
                    <th @click="sortTable('amber', 'nmi')">NMI <font-awesome-icon :icon="getSortIcon('nmi', 'amber')" /></th>
                    <th @click="sortTable('amber', 'plant_id')">Renox Plant ID <font-awesome-icon :icon="getSortIcon('plant_id', 'amber')" /></th>
                    <th @click="sortTable('amber', 'plant_name')">Plant Name <font-awesome-icon :icon="getSortIcon('plant_name', 'amber')" /></th>
                    <th @click="sortTable('amber', 'alias')">Alias <font-awesome-icon :icon="getSortIcon('alias', 'amber')" /></th>
                    <th>AI Pool Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in filteredAmberNMIs" :key="index" @click="navigateToDetails(item)" class="clickable-row">
                    <td>{{ item.nmi }}</td>
                    <td>{{ item.plant_id }}</td>
                    <td>{{ item.plant_name }}</td>
                    <td>{{ item.alias }}</td>
                    <td>
                      <span class="status-indicator">
                        <span class="status-circle status-active"></span>
                        In Pool
                      </span>
                    </td>
                  </tr>
                  <tr v-if="filteredAmberNMIs.length === 0">
                    <td colspan="5" class="no-data">No Amber NMIs found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiRequest } from '@/services/apiService';
import ModalBox from '@/components/cards/ModalBox.vue';
import ConfirmationDialogBox from '@/components/cards/ConfirmationDialogBox.vue';
import { eventBus } from '@/services/eventBus';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faSync,
  faInfoCircle,
  faTrash,
  faPlusCircle,
  faSort,
  faSortUp,
  faSortDown,
  faTimes,
  faCircleCheck,
  faLinkSlash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

library.add(
  faSync,
  faInfoCircle,
  faTrash,
  faPlusCircle,
  faSort,
  faSortUp,
  faSortDown,
  faTimes,
  faCircleCheck,
  faLinkSlash,
);

const LOCALVOLTS_SOURCE = 'LocalVolts API';

function nmiLookupKey(nmi) {
  const value = String(nmi ?? '').trim().toUpperCase();
  return value.length === 11 ? value.slice(0, 10) : value;
}

export default {
  name: 'NMIComparisonPage',
  components: {
    ModalBox,
    ConfirmationDialogBox,
    FontAwesomeIcon,
  },
  data() {
    return {
      isLoading: false,
      showModal: false,
      infoTitle: '',
      infoMessage: '',
      isDialogVisible: false,
      showDetailsModal: false,
      selectedNMI: {},
      selectedNMIToRemove: '',
      lastRefresh: null,
      poolMembers: [],
      subDataUrl: '/api/v1.1/data',
      plantNMIs: [],
      localVoltsNMIs: [],
      matchingNMIs: [],
      nonMatchingNMIs: [],
      activeTab: 'matchingTab',
      tabs: [
        {
          name: 'matchingTab',
          label: 'Matching NMIs',
          description: 'NMIs that exist in both Renox plant data and the LocalVolts retailer feed.',
        },
        {
          name: 'nonMatchingLocalVoltsTab',
          label: 'All Non-Matching LocalVolts NMIs',
          description: 'NMIs reported by LocalVolts that do not match any Renox plant NMI.',
        },
        {
          name: 'localVoltsAllTab',
          label: 'All LocalVolts NMIs',
          description: 'Complete list of NMIs returned by the LocalVolts API.',
        },
        {
          name: 'plantAllTab',
          label: 'All Renox NMIs',
          description: 'Complete list of NMIs registered in Renox plant data.',
        },
        {
          name: 'amberTab',
          label: 'All Amber NMIs',
          description: 'NMIs for AI pool members on the Amber retailer (retailer_id = 1).',
        },
      ],
      globalNmiSearch: '',
      searchMatchingNMI: '',
      searchNonMatchingLocalVoltsNMI: '',
      searchAllLocalVoltsNMI: '',
      searchAllPlantNMI: '',
      searchAmberNMI: '',
      sortConfig: {
        matching: { key: 'nmi', direction: 'asc' },
        nonMatching: { key: 'nmi', direction: 'asc' },
        localVoltsNMIs: { key: 'nmi', direction: 'asc' },
        plantNMIs: { key: 'nmi', direction: 'asc' },
        amber: { key: 'nmi', direction: 'asc' },
      },
    };
  },
  computed: {
    activeTabDescription() {
      const tab = this.tabs.find(t => t.name === this.activeTab);
      return tab ? tab.description : '';
    },
    totalRenoxNMIs() {
      return this.plantNMIs.length;
    },
    totalLocalVoltsNMIs() {
      return this.localVoltsNMIs.length;
    },
    totalNonMatchingLocalVoltsNMIs() {
      return this.nonMatchingNMIs.filter(item => item.source === LOCALVOLTS_SOURCE).length;
    },
    effectiveNmiLookupQuery() {
      return this.globalNmiSearch.trim().toUpperCase();
    },
    summaryCards() {
      return [
        {
          tabName: 'matchingTab',
          label: 'Matching NMIs',
          value: this.matchingNMIs.length,
          cardClass: 'matching-card',
          icon: ['fas', 'circle-check'],
        },
        {
          tabName: 'nonMatchingLocalVoltsTab',
          label: 'All Non-Matching LocalVolts NMIs',
          value: this.totalNonMatchingLocalVoltsNMIs,
          cardClass: 'non-matching-card',
          icon: ['fas', 'link-slash'],
        },
        {
          tabName: 'localVoltsAllTab',
          label: 'All LocalVolts NMIs',
          value: this.totalLocalVoltsNMIs,
          cardClass: 'localvolts-card',
          icon: ['fas', 'sync'],
        },
        {
          tabName: 'plantAllTab',
          label: 'All Renox NMIs',
          value: this.totalRenoxNMIs,
          cardClass: 'renox-card',
          icon: ['fas', 'info-circle'],
        },
        {
          tabName: 'amberTab',
          label: 'All Amber NMIs',
          value: this.amberNMIs.length,
          cardClass: 'amber-card',
          icon: ['fas', 'plus-circle'],
        },
      ];
    },
    amberNMIs() {
      const plantById = this.plantByIdMap;
      return this.poolMembers
        .filter(member => Number(member.retailer_id) === 1)
        .map(member => {
          const plant = plantById.get(Number(member.plant_id));
          return {
            nmi: plant ? plant.nmi : 'N/A',
            plant_id: member.plant_id,
            plant_name: plant ? plant.plant_name : 'N/A',
            alias: member.alias,
          };
        })
        .filter(item => item.nmi !== 'N/A');
    },
    plantByIdMap() {
      const map = new Map();
      this.plantNMIs.forEach(item => {
        map.set(Number(item.plant_id), item);
      });
      return map;
    },
    localVoltsNmiSet() {
      return new Set(this.localVoltsNMIs.map(item => nmiLookupKey(item.nmi)));
    },
    nmiLookupRows() {
      if (!this.effectiveNmiLookupQuery) return [];
      const presence = this.getNmiPresence(this.effectiveNmiLookupQuery);
      return [
        {
          key: 'renoxData',
          label: 'Renox Data',
          present: presence.renoxData,
          detail: presence.renoxDetail,
        },
        {
          key: 'renoxAiPool',
          label: 'Renox AI Pool',
          present: presence.renoxAiPool,
          detail: presence.poolDetail,
        },
        {
          key: 'amber',
          label: 'Amber',
          present: presence.amber,
          detail: presence.amberDetail,
        },
        {
          key: 'localVolts',
          label: 'LocalVolts',
          present: presence.localVolts,
          detail: presence.localVoltsDetail,
        },
      ];
    },
    filteredMatchingNMIs() {
      return this.filterAndSort(this.matchingNMIs, this.searchMatchingNMI, 'matching', ['nmi', 'plant_name']);
    },
    filteredNonMatchingLocalVoltsNMIs() {
      const localVoltsNonMatching = this.nonMatchingNMIs.filter(item => item.source === LOCALVOLTS_SOURCE);
      return this.filterAndSort(localVoltsNonMatching, this.searchNonMatchingLocalVoltsNMI, 'nonMatching', ['nmi']);
    },
    filteredAllLocalVoltsNMIs() {
      return this.filterAndSort(this.localVoltsNMIs, this.searchAllLocalVoltsNMI, 'localVoltsNMIs', ['nmi']);
    },
    filteredAllPlantNMIs() {
      return this.filterAndSort(this.plantNMIs, this.searchAllPlantNMI, 'plantNMIs', ['nmi', 'plant_id', 'plant_name']);
    },
    filteredAmberNMIs() {
      return this.filterAndSort(this.amberNMIs, this.searchAmberNMI, 'amber', ['nmi', 'plant_id', 'plant_name', 'alias']);
    },
  },
  methods: {
    async refreshData() {
      this.isLoading = true;
      await Promise.all([
        this.getPlantNMIList(),
        this.getLocalVoltsNMIData(),
        this.getPoolMembers(),
      ]);
      this.compareNMIs();
      this.lastRefresh = new Date();
      this.isLoading = false;
    },

    async getPlantNMIList() {
      try {
        const url = `${this.subDataUrl}/bridge/rx_data/plant/nmi/data_list/get`;
        const response = await apiRequest('post', url, {});
        if (response.errorCode === 0 && response.data) {
          this.plantNMIs = response.data.map(item => ({
            nmi: String(item.nmi).toUpperCase(),
            plant_id: item.plant_id,
            plant_name: item.plant_name,
          }));
        } else {
          this.showError('Failed to fetch plant NMI data');
        }
      } catch (error) {
        this.showError('Error fetching plant NMI list');
      }
    },

    async getPoolMembers() {
      try {
        const url = `${this.subDataUrl}/ai_pool_users/get`;
        const response = await apiRequest('post', url, {});
        if (response.errorCode === 0 && response.data) {
          this.poolMembers = response.data.map(item => ({
            plant_id: item.plant_id,
            alias: item.alias,
            ai_control_status: item.ai_control_status,
            retailer_id: item.retailer_id,
          }));
        } else {
          this.showError('Failed to fetch pool members data');
        }
      } catch (error) {
        this.showError('Error fetching pool members data');
      }
    },

    async getLocalVoltsNMIData() {
      try {
        const url = `${this.subDataUrl}/proxy/lv/get`;
        const response = await apiRequest('post', url, { nmi: '*' });
        if (response.length > 0) {
          const uniqueNMIs = Array.from(new Set(response.map(item => item.NMI)));
          this.localVoltsNMIs = uniqueNMIs.map(nmi => ({
            nmi: String(nmi).toUpperCase(),
            source: LOCALVOLTS_SOURCE,
          }));
        } else {
          this.showError('Failed to fetch LocalVolts NMI data');
        }
      } catch (error) {
        this.showError('Error fetching LocalVolts NMI data: ' + error);
      }
    },

    compareNMIs() {
      const lvNMISet = this.localVoltsNmiSet;

      this.matchingNMIs = this.plantNMIs.filter(item => this.isLocalVoltsMatch(item.nmi, lvNMISet));

      const matchingNMISet = new Set(this.matchingNMIs.map(item => nmiLookupKey(item.nmi)));

      this.nonMatchingNMIs = [];

      this.plantNMIs.forEach(item => {
        if (!matchingNMISet.has(nmiLookupKey(item.nmi))) {
          this.nonMatchingNMIs.push({
            nmi: item.nmi,
            plant_name: item.plant_name,
            source: 'Renox Plants API',
            missing_in: LOCALVOLTS_SOURCE,
          });
        }
      });

      this.localVoltsNMIs.forEach(item => {
        if (!matchingNMISet.has(nmiLookupKey(item.nmi))) {
          this.nonMatchingNMIs.push({
            nmi: item.nmi,
            source: LOCALVOLTS_SOURCE,
            missing_in: 'Renox Plants API',
          });
        }
      });
    },

    isLocalVoltsMatch(nmi, lvSet = null) {
      const set = lvSet || this.localVoltsNmiSet;
      return set.has(nmiLookupKey(nmi));
    },

    findPlantByNmi(nmi) {
      const key = nmiLookupKey(nmi);
      return this.plantNMIs.find(item => nmiLookupKey(item.nmi) === key);
    },

    getNmiPresence(nmi) {
      const normalized = String(nmi).trim().toUpperCase();
      const plant = this.findPlantByNmi(normalized);
      const renoxData = !!plant;

      let renoxAiPool = false;
      let poolDetail = '';
      let amber = false;
      let amberDetail = '';

      if (plant) {
        const poolMember = this.poolMembers.find(
          member => Number(member.plant_id) === Number(plant.plant_id),
        );
        if (poolMember) {
          renoxAiPool = true;
          poolDetail = poolMember.alias ? `Alias: ${poolMember.alias}` : `Plant ID: ${plant.plant_id}`;
          if (Number(poolMember.retailer_id) === 1) {
            amber = true;
            amberDetail = poolMember.alias ? `Alias: ${poolMember.alias}` : '';
          }
        }
      }

      const localVolts = this.isLocalVoltsMatch(normalized);
      const lvMatch = localVolts
        ? this.localVoltsNMIs.find(item => nmiLookupKey(item.nmi) === nmiLookupKey(normalized))
        : null;
      let localVoltsDetail = '';
      if (lvMatch && lvMatch.nmi !== normalized) {
        localVoltsDetail = `Stored as ${lvMatch.nmi}`;
      } else if (localVolts && plant && plant.nmi !== normalized) {
        localVoltsDetail = `Matched via Renox NMI ${plant.nmi}`;
      }

      const storedForm = plant && plant.nmi !== normalized ? ` · Stored as ${plant.nmi}` : '';

      return {
        renoxData,
        renoxDetail: plant ? `${plant.plant_name} (Plant ID: ${plant.plant_id})${storedForm}` : '',
        renoxAiPool,
        poolDetail,
        amber,
        amberDetail,
        localVolts,
        localVoltsDetail,
      };
    },

    clearNmiLookup() {
      this.globalNmiSearch = '';
    },

    goToTab(tabName) {
      this.activeTab = tabName;
      this.$nextTick(() => {
        const el = this.$refs.tabsContainer;
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },

    filterAndSort(items, searchTerm, sortKey, fields) {
      let filtered = items;
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const searchKey = nmiLookupKey(searchTerm);
        filtered = items.filter(item =>
          fields.some(field => {
            const value = item[field];
            if (!value) return false;
            if (String(value).toLowerCase().includes(search)) return true;
            return field === 'nmi' && nmiLookupKey(value).includes(searchKey);
          }),
        );
      }
      const config = this.sortConfig[sortKey];
      return this.sortItems(filtered, config.key, config.direction);
    },

    sortTable(tableType, key) {
      const config = this.sortConfig[tableType];
      if (config.key === key) {
        config.direction = config.direction === 'asc' ? 'desc' : 'asc';
      } else {
        config.key = key;
        config.direction = 'asc';
      }
    },

    sortItems(items, key, direction) {
      return [...items].sort((a, b) => {
        let valueA = a[key];
        let valueB = b[key];
        if (valueA < valueB) return direction === 'asc' ? -1 : 1;
        if (valueA > valueB) return direction === 'asc' ? 1 : -1;
        return 0;
      });
    },

    getSortIcon(key, tableType) {
      const config = this.sortConfig[tableType];
      if (config.key !== key) return ['fas', 'sort'];
      return config.direction === 'asc' ? ['fas', 'sort-up'] : ['fas', 'sort-down'];
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleString();
    },

    showConfirmationDialog(nmi) {
      this.selectedNMIToRemove = nmi;
      this.isDialogVisible = true;
    },

    handleConfirmed() {
      this.isDialogVisible = false;
      this.removeNMI(this.selectedNMIToRemove);
    },

    handleCancelled() {
      this.isDialogVisible = false;
      this.selectedNMIToRemove = '';
    },

    async removeNMI(nmi) {
      try {
        const url = `${this.subDataUrl}/nmi/monitoring/remove`;
        const response = await apiRequest('post', url, { nmi });
        if (response.errorCode === 0) {
          this.showSuccess('NMI successfully removed from monitoring');
          await this.refreshData();
        } else {
          this.showError(response.infoText || 'Failed to remove NMI');
        }
      } catch (error) {
        this.showError('Error removing NMI from monitoring');
      }
    },

    showError(message) {
      this.infoTitle = 'Error';
      this.infoMessage = message;
      this.showModal = true;
    },

    showSuccess(message) {
      this.infoTitle = 'Success';
      this.infoMessage = message;
      this.showModal = true;
    },

    isInPool(plantId) {
      const plantIdNum = Number(plantId);
      return this.poolMembers.some(member => Number(member.plant_id) === plantIdNum);
    },

    getRenoxPowerPortalUrl(row) {
      const plantName = (row.plant_name || '').replace(/ /g, '+');
      return `https://redxpower.com/#/plant/detail/${row.plant_id}?name=${plantName}`;
    },

    openRenoxPowerPortal(row) {
      window.open(this.getRenoxPowerPortalUrl(row), '_blank', 'noopener,noreferrer');
    },

    openRenoxAiPortal(row) {
      this.navigateToDetails(row);
    },

    navigateToDetails(row) {
      const poolMember = this.poolMembers.find(member => member.plant_id === Number(row.plant_id));
      const queryParams = {
        plant_id: row.plant_id,
        alias: poolMember ? poolMember.alias : row.plant_name,
      };
      eventBus.emit('nav-click', 1);
      this.$router.push({ name: 'DetailPage', query: queryParams });
    },
  },
  created() {
    this.refreshData();
    this.refreshInterval = setInterval(this.refreshData, 15 * 60 * 1000);
  },
  beforeUnmount() {
    clearInterval(this.refreshInterval);
  },
};
</script>

<style scoped>
.retailer-page-container {
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background-color: var(--bg-primary);
  color: var(--text-primary);
}

.page-subtitle {
  margin: 0.5rem 0 0;
  color: var(--text-tertiary);
  font-size: 0.9rem;
  line-height: 1.5;
}

.plant-info-card {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 12px var(--shadow-color);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid var(--border-color);
}

.plant-info-header {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-color);
}

.header-with-actions {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.plant-info-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-primary);
}

.plant-info-title svg {
  width: 24px;
  height: 24px;
  color: #667eea;
  flex-shrink: 0;
}

.plant-info-title h4 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
}

.plant-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  width: 100%;
}

.custom-kpi-card {
  background: var(--card-bg);
  border-radius: 20px;
  padding: 28px 24px;
  display: flex;
  align-items: center;
  gap: 20px;
  box-shadow: 0 12px 40px var(--shadow-color);
  border: 1px solid var(--border-color);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  min-height: 130px;
  cursor: pointer;
  text-align: left;
  width: 100%;
}

.custom-kpi-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, var(--card-accent), var(--card-accent-light));
  border-radius: 20px 20px 0 0;
}

.custom-kpi-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 60px rgba(0, 0, 0, 0.15);
}

.kpi-card-active {
  border-color: var(--card-accent);
  box-shadow: 0 16px 60px rgba(0, 0, 0, 0.12);
}

.matching-card {
  --card-accent: #667eea;
  --card-accent-light: #764ba2;
  --card-accent-rgb: 102, 126, 234;
}

.non-matching-card {
  --card-accent: #f093fb;
  --card-accent-light: #f5576c;
  --card-accent-rgb: 240, 147, 251;
}

.localvolts-card {
  --card-accent: #4ecdc4;
  --card-accent-light: #44a08d;
  --card-accent-rgb: 78, 205, 196;
}

.renox-card {
  --card-accent: #00f2fe;
  --card-accent-light: #4facfe;
  --card-accent-rgb: 79, 172, 254;
}

.amber-card {
  --card-accent: #ff9800;
  --card-accent-light: #f57c00;
  --card-accent-rgb: 255, 152, 0;
}

.kpi-icon {
  background: linear-gradient(135deg, var(--card-accent), var(--card-accent-light));
  border-radius: 18px;
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: white;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.2),
    0 0 20px rgba(var(--card-accent-rgb), 0.3);
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.custom-kpi-card:hover .kpi-icon {
  transform: scale(1.05);
}

.kpi-content {
  flex: 1;
  min-width: 0;
}

.kpi-value {
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
  margin-bottom: 8px;
  letter-spacing: -0.3px;
}

.kpi-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-secondary);
  line-height: 1.3;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  padding: 0.5rem 0.75rem;
  background: var(--bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  border-left: 3px solid #667eea;
}

.refresh-container {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.last-updated {
  color: var(--text-secondary);
  font-size: 0.85rem;
}

.refresh-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: var(--custom-color1);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 10px 16px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9rem;
  transition: transform 0.1s;
}

.refresh-button:hover {
  transform: translateY(-1px);
}

.clear-button {
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 10px 16px;
  cursor: pointer;
  font-size: 0.9rem;
}

.lookup-search-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 1rem;
}

.lookup-input {
  flex: 1;
  min-width: 200px;
}

.lookup-hint {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.lookup-results {
  margin-top: 1rem;
}

.lookup-query-label {
  margin: 0 0 0.75rem;
  font-size: 0.95rem;
  color: var(--text-primary);
}

.lookup-results-list {
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
  background: var(--bg-secondary);
}

.lookup-result-line {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.85rem 1.25rem;
  border-bottom: 1px solid var(--border-color);
  border-left: 3px solid transparent;
}

.lookup-result-line:last-child {
  border-bottom: none;
}

.lookup-result-line.present {
  border-left-color: var(--custom-color1);
}

.lookup-result-line.absent {
  border-left-color: #f44336;
  opacity: 0.9;
}

.lookup-line-label {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--text-primary);
  min-width: 140px;
  flex-shrink: 0;
}

.lookup-line-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: var(--text-secondary);
  min-width: 120px;
  flex-shrink: 0;
}

.lookup-line-detail {
  margin-left: auto;
  font-size: 0.85rem;
  color: var(--text-tertiary);
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.icon-present {
  color: var(--custom-color1);
  font-size: 1rem;
}

.icon-absent {
  color: #f44336;
  font-size: 1rem;
}

.date-range-tabs-container {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  overflow: hidden;
  border: 1px solid var(--border-color);
}

.tabs-header {
  display: flex;
  background: var(--bg-secondary);
  border-bottom: 2px solid var(--border-color);
  overflow-x: auto;
  scrollbar-width: none;
}

.tabs-header::-webkit-scrollbar {
  display: none;
}

.tab-button {
  flex: 1;
  min-width: 120px;
  padding: 14px 16px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  border-radius: 12px 12px 0 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  text-align: center;
}

.tab-button:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.tab-button.active {
  color: var(--custom-color1, #667eea);
  border-bottom-color: var(--custom-color1, #667eea);
  background: var(--card-bg);
  font-weight: 600;
}

.tab-content-panel {
  padding: 1.5rem;
  background: var(--card-bg);
  animation: fadeInContent 0.3s ease-in-out;
}

@keyframes fadeInContent {
  from { opacity: 0.7; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
}

.tab-description {
  margin: 0 0 1.25rem;
  color: var(--text-tertiary);
  font-size: 0.9rem;
  line-height: 1.5;
}

.tab-table-section {
  width: 100%;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  gap: 10px;
  flex-wrap: wrap;
}

.table-header h5 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
}

.search-container {
  display: flex;
  align-items: center;
}

.search-input {
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid var(--input-border);
  width: 250px;
  background-color: var(--input-bg);
  color: var(--text-primary);
  font-size: 0.9rem;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: var(--custom-color1);
  box-shadow: 0 0 0 3px rgba(32, 204, 32, 0.1);
}

.search-input::placeholder {
  color: var(--text-tertiary);
}

.table-container {
  width: 100%;
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.full-width-table {
  border-collapse: collapse;
  width: 100%;
}

th, td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid var(--border-color-light);
  color: var(--text-primary);
  font-size: 0.9rem;
}

th {
  background-color: var(--bg-secondary);
  color: var(--text-secondary);
  font-weight: 600;
  cursor: pointer;
  user-select: none;
}

th:hover {
  background-color: var(--bg-tertiary);
}

tbody tr:hover {
  background-color: var(--bg-secondary);
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-primary);
}

.status-circle {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.status-active {
  background-color: var(--custom-color1);
}

.status-inactive {
  background-color: #f44336;
}

.custom-button {
  padding: 10px 16px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 500;
}

.no-data {
  text-align: center;
  padding: 20px;
  color: var(--text-tertiary);
  font-style: italic;
}

.actions-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  min-width: 200px;
}

.action-link-button {
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s, transform 0.1s;
}

.action-link-button:hover {
  background: var(--bg-tertiary);
  transform: translateY(-1px);
}

.action-link-button-ai {
  border-color: var(--custom-color1);
  color: var(--custom-color1);
}

.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.clickable-row:hover {
  background-color: var(--bg-secondary) !important;
}

.dark-mode .clickable-row:hover {
  background-color: var(--bg-tertiary) !important;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.dark-mode .loading-overlay {
  background-color: rgba(0, 0, 0, 0.85);
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  font-size: 1.2rem;
  color: var(--custom-color1);
}

.loading-spinner span {
  color: var(--text-primary);
}

.loading-spinner svg {
  font-size: 2rem;
}

.fa-spin {
  animation: fa-spin 2s infinite linear;
}

@keyframes fa-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .header-with-actions {
    flex-direction: column;
  }

  .kpi-grid {
    grid-template-columns: 1fr;
  }

  .lookup-result-line {
    flex-wrap: wrap;
  }

  .lookup-line-detail {
    margin-left: 0;
    width: 100%;
    text-align: left;
    white-space: normal;
  }

  .table-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .search-input,
  .lookup-input {
    width: 100%;
  }

  .tab-button {
    min-width: 100px;
    font-size: 0.8rem;
    padding: 12px 10px;
  }
}
</style>
